import * as ngCore from '@angular/core'
import { Cache } from '@attain-apps/data-utils'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { TypeChecker } from '@attain-apps/type-guards'

import * as state from '../../../../../../../state'

/**
 * Multi reference component handles cases when we have couple of {@link ents.core.dms.models.Entity entities} in a
 * {@link ents.core.dms.models.Dimension dimension} and we want to show separate
 * {@link ds.input.select.multiple.Component multi select component} for each entity in the dimension.
 */
@ngCore.Component({
  imports: [ds.input.select.multiple.Component],
  selector: 'atn-multi-reference-input',
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.dms.schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(state.dms.schema.Feature)
  /** @see {@link state.display.Feature} */
  public readonly displayFeature = ngCore.inject(
    state.display.referenceRecords.Feature,
  )
  /** @see {@link ents.core.dms.data.Value.Type} */
  public readonly typeSg =
    ngCore.input.required<ents.core.dms.data.Value.Type.Reference.Multi>({
      alias: 'type',
    })
  /** @see {@link ents.core.dms.data.Value.Reference.Multi.Default} */
  public readonly valueSg = ngCore.model<
    ents.core.dms.data.Value.Reference.Multi.Default | undefined
  >(undefined, {
    alias: 'value',
  })
  /** @see {@link ents.core.dms.data.Value.Type.Reference.Multi.dimensionId}*/
  private readonly _refDimensionIdSg = ngCore.computed(
    (type = this.typeSg()) => type.dimensionId,
  )
  /**
   * The {@link ents.core.dms.models.Entity entities} in the
   * {@link ents.core.dms.models.Dimension dimension} pulled out from
   * {@link ents.core.dms.data.Value.Type.Reference.Multi multi reference}.
   */
  public readonly entitiesSg = ngCore.computed(
    (
      refDimensionId = this._refDimensionIdSg(),
      schema = this._schemaFeature.selectors.schema.asSignal(),
    ) => schema.getEntitiesInDimension(refDimensionId),
  )

  /**
   * {@link Cache} function that gets records as a list of {@link ds.input.select.models.Item items} from the state to display them in
   * the {@link ds.input.select.multiple.Component multi select component}
   */
  public readonly getRecordsFor = Cache.fn(
    ({
      entity,
      entityId,
    }: ExactlyOneOf<{
      entityId: ents.core.dms.models.Entity.Id
      entity: ents.core.dms.models.Entity
    }>) => {
      entityId ??= entity?.id

      if (entityId === undefined) {
        throw new Error('Either entity or entityId must be provided')
      }

      return this.displayFeature.selectors
        .getRecords({ entityId })
        .project(records =>
          records.map<ds.input.select.models.Item>(record => ({
            label:
              state.display.referenceRecords.models.Record.toBreadcrumb(record),
            value: record.id,
          })),
        )
    },
  )

  constructor() {
    ngCore.effect(() => {
      // FIXME: Remove this effect once multi-reference default values are fixed.
      // Currently, default values come in invalid format:
      // { entityId: undefined, recordId: undefined }
      // instead of expected format: { [entityId: string]: { recordIds: string[] } }
      const value = this.valueSg() ?? {}
      delete (value as any).entityId
      delete (value as any).recordId

      if (Object.keys(value).length === 0) {
        this.valueSg.set(undefined)
      }
    })
    /**
     * Loads records for every {@link ents.core.dms.models.Entity entity} in {@link entitiesSg}
     */
    ngCore.effect(() => {
      this.entitiesSg().forEach(({ id }) =>
        this.displayFeature.actions.loadRecords
          .from('display.value.input.reference.Init (Component)')
          .dispatch({ cacheFirst: true, entityId: id }),
      )
    })
  }

  /**
   * Refetch the record items for the currently selected entity IDs.
   */
  public refetchRecordItemsClickHandler({
    entityId,
    entity,
  }: ExactlyOneOf<{
    entityId: ents.core.dms.models.Entity.Id
    entity: ents.core.dms.models.Entity
  }>) {
    entityId ??= entity?.id
    if (entityId === undefined) {
      throw new Error('Either entity or entityId must be provided')
    }

    this.displayFeature.actions.loadRecords
      .from('display.value.input.reference.Init (Component)')
      .dispatch({ entityId })
  }

  /** Updating {@link valueSg} map upon selection change */
  public valueChangeHandler({
    entity,
    recordIds,
  }: {
    entity: ents.core.dms.models.Entity
    recordIds: readonly unknown[]
  }) {
    this.valueSg.update(oldValue => {
      // in case the value is undefined, initialize it
      oldValue ??= {}
      // unseal the value object to allow modifications
      oldValue = { ...oldValue }

      // NOTE: Our select component's can only return readonly unknown[] so we
      // have to typecheck it or assert
      const _isRecordIds: TypeChecker<readonly ents.core.dms.data.Record.Id[]> =
        TypeChecker.Factory.list(ents.core.dms.data.Record.Id.isId)

      if (recordIds.length && _isRecordIds(recordIds)) {
        oldValue[entity.id] = recordIds
      } else {
        // NOTE: We want to treat records that have { entityID: [] } as if that
        // filter doesn't exist, hence we remove that entityID from record
        delete oldValue[entity.id]
      }

      // If the value object has no entries, set it to undefined instead of an
      // empty object
      return Object.keys(oldValue).length === 0 ? undefined : oldValue
    })
  }
}
